import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowUpRight, Wrench, Clock, FileText, Search, X, Filter,
} from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import { TOOLS } from "@/lib/tools/tool-config";
import { useToolSeo } from "@/components/tools/seo";
import { track } from "@/lib/analytics";
import useExperimentVariant, { fireExperimentEvent } from "@/hooks/useExperimentVariant";
import { fetchCalculatorCatalog } from "@/lib/calculatorApi";
import { CATEGORY_META, categoryLabel, categoryIcon } from "@/lib/tools/category-meta";

// Legacy → engine slug redirects (must match the Navigate routes in App.js)
// so the Hub doesn't render duplicate cards for both old + new slugs.
const LEGACY_TO_ENGINE = {
    "home-inspection-negotiation-calculator": "seller-credit-estimator",
    "should-i-buy-this-house-inspection-quiz": "walk-away-from-house-calculator",
    "roof-repair-cost-calculator": "roof-repair-cost-estimator",
    "hvac-age-replacement-checker": "hvac-replacement-cost-calculator",
    "electrical-panel-concern-checker": "electrical-panel-upgrade-cost-calculator",
};

export default function ToolsHub() {
    const [engineCalcs, setEngineCalcs] = useState([]);
    const [query, setQuery] = useState("");
    const [activeCategories, setActiveCategories] = useState(() => new Set());

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await fetchCalculatorCatalog();
                if (!cancelled) setEngineCalcs(r?.items || []);
            } catch (_) { /* hub gracefully degrades to static TOOLS */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const mergedTools = useMemo(() => {
        const out = [];
        const seen = new Set();
        for (const c of engineCalcs) {
            seen.add(c.slug);
            out.push({
                slug: c.slug,
                name: c.title,
                short: c.short_description,
                samplePreview: null,
                time: "2-3 minutes",
                category: c.category,
                cover: c.pinterest?.image_url || null,
                isEngine: true,
            });
        }
        for (const t of TOOLS) {
            const canonical = LEGACY_TO_ENGINE[t.slug] || t.slug;
            if (seen.has(canonical)) continue;
            seen.add(t.slug);
            out.push({ ...t, cover: null, isEngine: false });
        }
        return out;
    }, [engineCalcs]);

    // Category buckets present in the merged catalog (with counts).
    const categoriesWithCounts = useMemo(() => {
        const counts = {};
        for (const t of mergedTools) {
            counts[t.category] = (counts[t.category] || 0) + 1;
        }
        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .map(([key, count]) => ({
                key,
                count,
                label: categoryLabel(key),
                Icon: categoryIcon(key),
            }));
    }, [mergedTools]);

    // Filtered & searched list.
    const visibleTools = useMemo(() => {
        const q = query.trim().toLowerCase();
        return mergedTools.filter((t) => {
            if (activeCategories.size > 0 && !activeCategories.has(t.category)) {
                return false;
            }
            if (!q) return true;
            const hay = (t.name + " " + (t.short || "") + " " + categoryLabel(t.category)).toLowerCase();
            return hay.includes(q);
        });
    }, [mergedTools, query, activeCategories]);

    const toggleCategory = (key) => {
        setActiveCategories((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            track("tools_hub_filter_toggled", {
                category: key,
                active_now: !prev.has(key),
                active_count: next.size,
            });
            return next;
        });
    };

    const clearFilters = () => {
        setActiveCategories(new Set());
        setQuery("");
        track("tools_hub_filters_cleared");
    };

    useToolSeo({
        title:
            "Free Home Inspection Tools — Repair Costs, Negotiation, Risk | Buyer's Leverage",
        description:
            "Free, rule-based home inspection tools for buyers. Estimate repair exposure, plan negotiation, check foundation cracks, water intrusion, roof, HVAC, and electrical concerns. Mobile-first. No upload required.",
        canonical:
            typeof window !== "undefined"
                ? `${window.location.origin}/tools`
                : undefined,
        structuredData: {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Free Home Inspection Tools",
            description:
                "Suite of free, rule-based home inspection calculators and risk checkers for buyers.",
            url:
                typeof window !== "undefined"
                    ? `${window.location.origin}/tools`
                    : undefined,
        },
    });

    const { experiments } = useExperimentVariant("tools_landing");
    const ctaVariant = experiments[0];

    useEffect(() => {
        track("tools_hub_viewed", { tool_count: mergedTools.length });
    }, [mergedTools.length]);

    const filtersActive = activeCategories.size > 0 || query.trim().length > 0;

    return (
        <PublicLayout>
            {/* Hero */}
            <section
                className="bl-section pt-12 sm:pt-16 lg:pt-20 pb-8 bg-bl-bg"
                data-testid="tools-hub-hero"
            >
                <div className="bl-container">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                        <div className="lg:col-span-7" data-reveal="slide-left-lg">
                            <div className="bl-eyebrow inline-flex items-center gap-2">
                                <Wrench className="h-3.5 w-3.5" />
                                Free Tools
                            </div>
                            <h1
                                className="mt-5 font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-bl leading-[1.05]"
                                data-testid="tools-hub-h1"
                            >
                                Free home inspection tools.
                            </h1>
                            <p className="mt-5 text-base sm:text-lg text-bl-muted leading-relaxed">
                                Estimate repair exposure, organize inspection concerns,
                                and prepare for smarter negotiation decisions. Calm,
                                rule-based, buyer-first — no upload required.
                            </p>
                            <div className="mt-7 flex flex-wrap gap-3">
                                <Link
                                    to="/upload"
                                    className="bl-btn-primary gap-2"
                                    data-testid="tools-hub-upload-cta"
                                >
                                    <FileText className="h-4 w-4" />
                                    Or upload your report for full analysis
                                </Link>
                                <Link
                                    to="/sample-analysis"
                                    className="bl-btn-secondary gap-2"
                                    data-testid="tools-hub-sample-cta"
                                >
                                    View sample report
                                    <ArrowUpRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </div>

                        <div className="lg:col-span-5" data-reveal="slide-right-lg" data-reveal-delay="2">
                            <div
                                className="overflow-hidden rounded-3xl border border-bl shadow-sm bg-bl-cream"
                                data-testid="tools-hero-image-wrap"
                            >
                                <picture>
                                    <source srcSet="/tools-hero-claymation.webp" type="image/webp" />
                                    <img
                                        src="/tools-hero-claymation.png"
                                        alt="Claymation scene of a homeowner reviewing an inspection report with Buyer's Leverage calculators — repair cost, risk score, seller credit, negotiation leverage, and foundation & roof indicators — analyzing the property."
                                        loading="eager"
                                        fetchPriority="high"
                                        className="w-full h-auto block"
                                        data-testid="tools-hero-image"
                                    />
                                </picture>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Filters row — search + multi-select category chips */}
            <section className="bg-bl-bg pb-6 sticky top-0 z-20" data-testid="tools-hub-filters">
                <div className="bl-container">
                    <div
                        className="rounded-2xl border border-bl bg-bl-surface p-4 sm:p-5 shadow-sm"
                    >
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <div className="relative flex-1 min-w-0">
                                <Search
                                    className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-bl-muted pointer-events-none"
                                    aria-hidden="true"
                                />
                                <input
                                    type="search"
                                    value={query}
                                    onChange={(e) => {
                                        setQuery(e.target.value);
                                        if (e.target.value.length === 3) {
                                            track("tools_hub_search_started");
                                        }
                                    }}
                                    placeholder="Search tools by name, topic, or category…"
                                    className="w-full rounded-full border border-bl bg-white pl-10 pr-10 py-2.5 text-sm text-bl placeholder:text-bl-muted/70 focus:outline-none focus:ring-2 focus:ring-bl-primary/40 focus:border-bl-primary"
                                    data-testid="tools-hub-search-input"
                                    aria-label="Search tools"
                                />
                                {query && (
                                    <button
                                        type="button"
                                        onClick={() => setQuery("")}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-bl-muted hover:text-bl"
                                        data-testid="tools-hub-search-clear"
                                        aria-label="Clear search"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>

                            <div className="flex items-center gap-2 sm:justify-end shrink-0">
                                <Filter className="h-3.5 w-3.5 text-bl-muted" />
                                <span className="text-xs text-bl-muted whitespace-nowrap">
                                    {visibleTools.length} of {mergedTools.length} tools
                                </span>
                                {filtersActive && (
                                    <button
                                        type="button"
                                        onClick={clearFilters}
                                        className="text-xs text-bl-primary hover:underline whitespace-nowrap"
                                        data-testid="tools-hub-clear-filters"
                                    >
                                        Clear all
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Category chip row — horizontally scrolling on mobile */}
                        <div
                            className="mt-4 -mx-1 px-1 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                            data-testid="tools-hub-categories"
                            role="tablist"
                            aria-label="Filter tools by category"
                        >
                            {categoriesWithCounts.map(({ key, count, label, Icon }) => {
                                const active = activeCategories.has(key);
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        role="tab"
                                        aria-pressed={active}
                                        onClick={() => toggleCategory(key)}
                                        data-testid={`category-chip-${key}`}
                                        className={[
                                            "inline-flex items-center gap-2 whitespace-nowrap rounded-full",
                                            "px-3.5 py-1.5 text-sm font-medium transition-all border",
                                            active
                                                ? "bg-bl-primary text-white border-bl-primary shadow-sm"
                                                : "bg-white text-bl border-bl hover:border-bl-primary/40 hover:bg-bl-cream",
                                        ].join(" ")}
                                    >
                                        <Icon className="h-3.5 w-3.5" />
                                        <span className="uppercase tracking-wide text-[11px] font-semibold">
                                            {label}
                                        </span>
                                        <span
                                            className={[
                                                "text-[10px] font-mono-bl tabular-nums px-1.5 py-0.5 rounded-full",
                                                active
                                                    ? "bg-white/20 text-white"
                                                    : "bg-bl-cream text-bl-muted",
                                            ].join(" ")}
                                        >
                                            {count}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* Tool list — horizontal cards stacked vertically */}
            <section className="bl-section pt-2 pb-16 sm:pb-20">
                <div className="bl-container">
                    {visibleTools.length === 0 ? (
                        <div
                            className="bl-card p-8 sm:p-12 text-center max-w-xl mx-auto"
                            data-testid="tools-hub-empty"
                        >
                            <Search className="h-6 w-6 text-bl-muted mx-auto" />
                            <h2 className="mt-4 font-display font-semibold text-xl text-bl">
                                No tools match those filters.
                            </h2>
                            <p className="mt-2 text-sm text-bl-muted">
                                Try clearing your search or selecting a different category.
                            </p>
                            <button
                                type="button"
                                onClick={clearFilters}
                                className="bl-btn-secondary mt-5"
                                data-testid="tools-hub-empty-clear"
                            >
                                Clear filters
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4" data-testid="tools-grid">
                            {visibleTools.map((tool, i) => {
                                const Icon = categoryIcon(tool.category);
                                return (
                                    <Link
                                        key={tool.slug}
                                        to={`/tools/${tool.slug}`}
                                        className="group block rounded-2xl border border-bl bg-bl-surface hover:border-bl-primary/40 hover:shadow-md transition-all overflow-hidden"
                                        data-testid={`tool-card-${tool.slug}`}
                                        data-reveal="rise-sm"
                                        data-reveal-delay={Math.min(5, (i % 5) + 1)}
                                    >
                                        <div className="flex flex-col sm:flex-row sm:items-stretch">
                                            {/* Left: category icon block (vertical color band) */}
                                            <div className="flex sm:flex-col items-center justify-center gap-2 px-5 sm:px-6 py-4 sm:py-6 bg-bl-cream sm:w-44 sm:shrink-0 border-b sm:border-b-0 sm:border-r border-bl">
                                                <div className="w-10 h-10 rounded-full bg-bl-primary/10 text-bl-primary flex items-center justify-center shrink-0">
                                                    <Icon className="h-5 w-5" />
                                                </div>
                                                <div className="uppercase tracking-[0.16em] text-[10px] sm:text-[11px] font-semibold text-bl text-center">
                                                    {categoryLabel(tool.category)}
                                                </div>
                                            </div>

                                            {/* Middle: text content */}
                                            <div className="flex-1 min-w-0 px-5 sm:px-6 py-5 sm:py-6">
                                                <div className="flex items-start justify-between gap-4">
                                                    <h2 className="font-display font-semibold text-lg sm:text-xl leading-snug tracking-tight text-bl">
                                                        {tool.name}
                                                    </h2>
                                                    <ArrowUpRight className="h-4 w-4 text-bl-muted shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                                </div>
                                                <p className="mt-2 text-sm text-bl-muted leading-relaxed">
                                                    {tool.short}
                                                </p>
                                                {tool.samplePreview && (
                                                    <div
                                                        className="mt-3 rounded-lg bg-bl-cream border-l-2 border-bl-primary px-3 py-2 text-xs font-mono-bl text-bl leading-relaxed"
                                                        data-testid={`tool-sample-${tool.slug}`}
                                                    >
                                                        “{tool.samplePreview}”
                                                    </div>
                                                )}
                                                <div className="mt-3 flex items-center gap-4 text-xs text-bl-muted">
                                                    <span className="inline-flex items-center gap-1">
                                                        <Clock className="h-3 w-3" />
                                                        {tool.time}
                                                    </span>
                                                    <span className="text-bl-primary font-medium group-hover:underline">
                                                        Use tool →
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    )}

                    {/* Bottom CTA */}
                    <div
                        className="mt-12 sm:mt-16 bl-card p-6 sm:p-8 bg-[var(--bl-teal-tint)] border-2 border-bl-primary/30 max-w-3xl mx-auto text-center"
                        data-reveal="scale"
                    >
                        <h2 className="font-display font-semibold text-2xl text-bl tracking-tight">
                            {ctaVariant?.config?.headline || "Need a report-specific answer?"}
                        </h2>
                        <p className="mt-3 text-sm text-bl-muted leading-relaxed">
                            {ctaVariant?.config?.body ||
                                "These free tools use rule-based guidance. Upload your inspection PDF for a prioritized issue list, repair exposure ranges, and negotiation guidance built from your actual report."}
                        </p>
                        <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
                            <Link
                                to="/upload"
                                onClick={() => {
                                    if (ctaVariant) {
                                        fireExperimentEvent(
                                            ctaVariant.experiment_id,
                                            ctaVariant.variant_id,
                                            "tools_upload_cta_clicked",
                                            null,
                                            { location: "tools_hub_bottom" },
                                        );
                                    }
                                }}
                                className="bl-btn-primary gap-2 justify-center"
                                data-testid="tools-hub-bottom-upload"
                            >
                                <FileText className="h-4 w-4" />
                                {ctaVariant?.config?.primary_label ||
                                    "Upload your inspection"}
                            </Link>
                            <Link
                                to="/pricing"
                                className="bl-btn-secondary gap-2 justify-center"
                            >
                                See pricing
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
}
